import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { FabricThemeProvider } from '@fabric-ui/theme';

type ThemeMode = 'light' | 'dark';

interface ThemeModeContextType {
  mode: ThemeMode;
  toggleMode: () => void;
}

const LS_KEY = 'themeMode';

const ThemeModeContext = createContext<ThemeModeContextType | undefined>(undefined);

export const useThemeMode = () => {
  const context = useContext(ThemeModeContext);
  if (context === undefined) {
    throw new Error('useThemeMode must be used within a AppThemeProvider');
  }
  return context;
};

export const AppThemeProvider = ({ children }: React.PropsWithChildren) => {
  const [mode, setMode] = useState<ThemeMode>((localStorage.getItem(LS_KEY) as ThemeMode) || 'light');

  const themeModeValue = useMemo(
    () => ({
      mode,
      toggleMode: () => {
        setMode(prevMode => (prevMode === 'light' ? 'dark' : 'light'));
      },
    }),
    [mode]
  );

  useEffect(() => {
    localStorage.setItem(LS_KEY, mode);
    document.documentElement.dataset.mode = mode;
  }, [mode]);

  return (
    <ThemeModeContext.Provider value={themeModeValue}>
      <FabricThemeProvider>{children}</FabricThemeProvider>
    </ThemeModeContext.Provider>
  );
};
